import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="group block cursor-pointer"
      >
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画在线阅读`}
            title={`${comic.title} - 免费韩漫`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {comic.isHot && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-primary-500 text-background-50 text-[10px] font-medium rounded whitespace-nowrap">
              HOT
            </span>
          )}
          {comic.isNew && (
            <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded whitespace-nowrap">
              NEW
            </span>
          )}
          {comic.status === '已完结' && (
            <span className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-foreground-900/70 text-background-50 text-[10px] font-medium rounded whitespace-nowrap">
              已完结
            </span>
          )}
        </div>
        <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">更新至{comic.chapters.length}话</p>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${comic.id}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="relative w-20 h-28 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={comic.cover}
            alt={`${comic.title} - 韩国漫画在线阅读`}
            title={`${comic.title} - 免费韩漫`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
            {comic.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{comic.description}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] text-accent-600 bg-accent-100 px-1.5 py-0.5 rounded whitespace-nowrap">
              {comic.category}
            </span>
            <span className="text-[10px] text-foreground-500 whitespace-nowrap">
              {comic.chapters.length}话 · {comic.status}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
        <img
          src={comic.cover}
          alt={`${comic.title} - 韩国漫画在线阅读`}
          title={`${comic.title} - 免费韩漫`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent" />
        {comic.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
            HOT
          </span>
        )}
        {comic.isNew && !comic.isHot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded whitespace-nowrap">
            NEW
          </span>
        )}
        <div className="absolute bottom-2 left-2 right-2">
          <div className="flex items-center gap-1 text-background-50 text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-accent-400 text-[10px]"></i>
            </div>
            <span>{comic.rating}</span>
            <span className="mx-1">·</span>
            <span>{comic.category}</span>
          </div>
        </div>
        {comic.status === '已完结' && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-foreground-900/70 text-background-50 text-xs font-medium rounded whitespace-nowrap">
            已完结
          </span>
        )}
      </div>
      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
        {comic.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1">
        更新至{comic.chapters.length}话 · {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}次阅读
      </p>
    </Link>
  );
}